import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { enums } from '@kattana-io/common';
import { IStore } from '../../../reducers';
import useTicker from '../../../hooks/useTicker';
import PairIcons from '../../../molecules/PairIcons';
import Text from '../../../atoms/Text';
import WatchlistStar from '../../Modal/TickersModal/WatchlistStar';
import { onCopyAddress, onWalletScan } from '../../../helpers/addressUtils';
import useTickerLivePrice from '../../Watchlist/hooks/useTickerLivePrice';
import NumberAnimation from '../../../molecules/NumberAnimation';
import { formatAddress } from '../../../helpers/formatters';
import NumberDisplay from '../../../molecules/NumberDisplay';

import './styles.scss';

type PriceColor = 'grey' | 'green' | 'red';

const getPriceColor = (side?: 'buy' | 'sell'): PriceColor => {
  switch (side) {
  case 'buy':
    return 'green';
  case 'sell':
    return 'red';
  default:
    return 'grey';
  }
};

const ChartHeader = () => {
  const trading = useSelector((state: IStore) => state.registry.trending);
  const current = useSelector((state: IStore) => state.current);
  const {
    chain,
    name,
    base,
    quote,
    base_address,
    quote_address,
    base_slug,
    quote_slug,
  } = useTicker(current);

  const { price, side } = useTickerLivePrice(current);

  return (
    <div className='chart_header'>
      <div className='chart_header__container'>
        <div className='chart_header__container__left'>
          <PairIcons
            pair={name}
            chain={chain}
            base_address={base_address}
            quote_address={quote_address}
            base_slug={base_slug}
            quote_slug={quote_slug}
          />
          <div
            className={`chart_header__pair ${
              current.kind === enums.ETickerKind.CEX
                ? 'chart_header__pair--disabled'
                : ''
            }`}
          >
            <Link to={`/en/token/${chain}/${base_address}`}>
              <Text size={14} className='chart_header__pair--base'>
                {base}
              </Text>
            </Link>
            <Text size={14} className='chart_header__pair--slash'>
              &nbsp;/&nbsp;
            </Text>
            <Link to={`/en/token/${chain}/${quote_address}`}>
              <Text size={14} className='chart_header__pair--quote'>
                {quote}
              </Text>
            </Link>
          </div>
          <div className='chart_header__info'>
            {!trading.some((item) => item.ticker?.id === current.id) && (
              <div className='chart_header__info__star'>
                <WatchlistStar ticker={current} />
              </div>
            )}
            {current.kind === enums.ETickerKind.DEX && (
              <>
                <Text
                  size={12}
                  className='chart_header__info__address'
                  style={{
                    'data-for': 'chart-header-tooltip',
                    'data-tip': current.address,
                  }}
                >
                  {formatAddress(current.address)}
                </Text>
                <ReactTooltip
                  id='chart-header-tooltip'
                  type='light'
                  place='top'
                  effect='solid'
                  className='text_tooltip'
                />
                <div className='chart_header__info__actions'>
                  <div
                    className='chart_header__info__actions__item icon-copy'
                    onClick={() => onCopyAddress(current.address)}
                  />
                  <div
                    className='chart_header__info__actions__item icon-top-right-square'
                    onClick={() => onWalletScan(current.address, chain)}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className='chart_header__statistics'>
          <Text size={12} className='text--grey-400'>
            <Text size={12} className='text--weight-500'>
              Vol:
            </Text>
            &nbsp;
            <NumberDisplay
              value={current.volume}
              round={2}
              endSymbol='$'
              string
            />
          </Text>
          <Text size={12} className='text--grey-400'>
            <Text size={12} className='text--weight-500'>
              Liq:
            </Text>
            &nbsp;
            <NumberDisplay
              value={current.liquidity}
              round={2}
              endSymbol='$'
              string
            />
          </Text>
        </div>
        <div className='chart_header__container__right'>
          <NumberAnimation
            value={price.rawPrice}
            startSymbol={price.sign}
            textProps={{
              size: 18,
              className: `chart_header__price chart_header__price--${getPriceColor(
                side
              )}`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ChartHeader;
